import React, { useState } from 'react'
import getAgeByBirthdayDate from 'common/utils/getAgeByBirthdayDate'
import Modal from 'presentation/shared/components/Modal'
import Heading from 'presentation/shared/components/Heading'
import { cpfMask, dateMask, phoneMask } from 'presentation/utils/masks'
import { maritalStatus } from 'presentation/utils/default-marital-status'
import { useFormik } from 'formik'
import { Patient } from 'domain/entities/patient-model'
import moment from 'moment'
import * as S from './styles'
import * as yup from 'yup'
import { Gender } from 'common/enum/gender'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'

type Props = {
  showModal(show: boolean): void
  patientData: Patient
  changePatientData: (data: ModifiedPatientData) => void
}

export default function EditPatientDataModal({
  showModal,
  patientData,
  changePatientData
}: Props) {
  const birthDay = moment(patientData.birthday).format('DD/MM/YYYY')

  const formik = useFormik({
    initialValues: {
      patient: patientData,
      cpf: patientData.cpf,
      name: patientData.name,
      gender: patientData.gender,
      phone: patientData.phone,
      email: patientData.email,
      maritalStatus: patientData.maritalStatus,
      landlinePhone: patientData.landlinePhone,
      birthday: birthDay
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values) => {
      const newData = {
        ...values,
        birthday: moment(values.birthday, 'DD/MM/YYYY').toISOString(),
        phone: values.phone?.replace(/\D/g, ''),
        landlinePhone: values.landlinePhone?.replace(/\D/g, '')
      }
      changePatientData(newData)
    }
  })
  const [onlyVisualization] = useState(true)
  const [modalVisible] = useState(true)
  const age = getAgeByBirthdayDate(formik.values.birthday)
  const genderList = [
    { label: 'Masculino', value: 'Masculino' },
    { label: 'Feminino', value: 'Feminino' }
  ]

  return (
    <Modal show={modalVisible} preventAutomateClose style={{ width: '50%' }}>
      <Heading
        style={{ textAlign: 'left', alignSelf: 'flex-start' }}
        size="large"
        color="gray"
        as="h1"
      >
        Dados do paciente
      </Heading>
      <S.Wrapper>
        <S.InputsContainer>
          <TextFieldNew
            key="cpf"
            label="CPF"
            labelSize="xsmall"
            labelOverlapping
            name="cpf"
            mask={cpfMask}
            defaultValue={formik.values.cpf}
            disabled={onlyVisualization}
          />
          <TextFieldNew
            key="name"
            label="Paciente"
            labelSize="xsmall"
            labelOverlapping
            name="name"
            onChange={formik.handleChange}
            defaultValue={formik.values.name}
            required
          />
          <SelectFieldNew
            label="Sexo"
            labelSize="xsmall"
            labelOverlapping
            name="gender"
            items={genderList}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            defaultValue={formik.values.gender}
            required
          />
        </S.InputsContainer>
        <S.InputsContainer>
          <SelectFieldNew
            label="Estado civil"
            labelSize="xsmall"
            labelOverlapping
            name="maritalStatus"
            items={maritalStatus}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            defaultValue={formik.values.maritalStatus}
            required
            disabled
          />
          <TextFieldNew
            key="birthday"
            label="Data de nascimento"
            labelSize="xsmall"
            labelOverlapping
            name="birthday"
            onChange={formik.handleChange}
            defaultValue={birthDay}
            mask={dateMask}
            required
          />
          <TextFieldNew
            key="age"
            label="Idade"
            labelSize="xsmall"
            labelOverlapping
            name="age"
            disabled={onlyVisualization}
            value={age || '-'}
            style={{ width: 75 }}
          />
        </S.InputsContainer>
        <S.InputsContainer>
          <TextFieldNew
            key="email"
            label="E-mail"
            labelSize="xsmall"
            labelOverlapping
            name="email"
            onChange={formik.handleChange}
            defaultValue={formik.values.email}
            required
          />
          <TextFieldNew
            key={'phone'}
            label="Celular"
            labelSize="xsmall"
            labelOverlapping
            name="phone"
            onChange={formik.handleChange}
            defaultValue={formik.values.phone}
            mask={phoneMask}
            required
          />
          <TextFieldNew
            key="landlinePhone"
            label="Telefone fixo"
            labelSize="xsmall"
            labelOverlapping
            name="landlinePhone"
            onChange={formik.handleChange}
            defaultValue={formik.values.landlinePhone}
            mask={phoneMask}
          />
        </S.InputsContainer>
        <S.ActionsContainer>
          <ButtonNew outlined onClick={() => showModal(false)}>
            Cancelar
          </ButtonNew>
          <ButtonNew
            style={{ width: '25%' }}
            onClick={() => formik.submitForm()}
            type="submit"
            disabled={!formik.isValid}
          >
            <S.ButtonTitle>Salvar</S.ButtonTitle>
          </ButtonNew>
        </S.ActionsContainer>
      </S.Wrapper>
    </Modal>
  )
}

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  email: yup.string().email().required(),
  phone: yup
    .string()
    .transform((value) => value.replace(/\D/g, '')) // remove máscara
    .matches(
      /^\d{4}9\d{8}$/,
      'O telefone deve estar no formato correto com DDI + DDD + número iniciando com 9'
    )
    .required(),
  gender: yup.string().required(),
  maritalStatus: yup.string().required(),
  birthday: yup.date().format('DD/MM/YYYY', true).required()
})

export type ModifiedPatientData = {
  patient: Patient
  cpf: string
  name: string
  gender: Gender
  phone?: string
  email: string
  maritalStatus?: string
  landlinePhone?: string
  birthday: string
}
